import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import RouterIcon from "@mui/icons-material/Router";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import RefreshIcon from "@mui/icons-material/Refresh";
import LinkOffIcon from "@mui/icons-material/LinkOff";

import useTr069Device from "../../../hooks/useTr069Device";
import { showSuccessSnackbar, showErrorSnackbar } from "../../../redux/slices/snackbar";

function Tr069Device({ radUserId }) {
  const dispatch = useDispatch();
  const {
    device,
    tasks,
    isLoading,
    isSubmitting,
    getDeviceByRadUser,
    linkDevice,
    unlinkDevice,
    getDeviceTasks,
    refreshParameters,
    rebootDevice,
    factoryResetDevice,
  } = useTr069Device();

  const [serialNumber, setSerialNumber] = useState("");

  const fetchDevice = async () => {
    if (radUserId) {
      await getDeviceByRadUser(radUserId);
    }
  };

  useEffect(() => {
    fetchDevice();
  }, [radUserId]);

  useEffect(() => {
    if (device?.deviceId) {
      getDeviceTasks(device.deviceId);
    }
  }, [device?.deviceId]);

  const handleLink = async () => {
    if (!serialNumber.trim()) {
      return;
    }
    try {
      await linkDevice(radUserId, serialNumber.trim());
      dispatch(showSuccessSnackbar("Router linked to this customer"));
      setSerialNumber("");
    } catch (exception) {
      dispatch(showErrorSnackbar(exception?.message || "Could not find/link that router"));
    }
  };

  const handleUnlink = async () => {
    if (!window.confirm("Unlink this router from the customer?")) {
      return;
    }
    try {
      await unlinkDevice(device.deviceId);
      dispatch(showSuccessSnackbar("Router unlinked"));
    } catch (exception) {
      dispatch(showErrorSnackbar("Failed to unlink router"));
    }
  };

  const handleRefresh = async () => {
    try {
      await refreshParameters(
        device.deviceId,
        (device.parameters || []).map((parameter) => parameter.name),
      );
      dispatch(showSuccessSnackbar("Refresh requested — router will report values shortly"));
    } catch (exception) {
      dispatch(showErrorSnackbar("Failed to queue refresh"));
    }
  };

  const handleReboot = async () => {
    if (!window.confirm("Reboot this router now?")) {
      return;
    }
    try {
      await rebootDevice(device.deviceId);
      dispatch(showSuccessSnackbar("Reboot queued"));
    } catch (exception) {
      dispatch(showErrorSnackbar("Failed to queue reboot"));
    }
  };

  const handleFactoryReset = async () => {
    if (!window.confirm("Factory reset this router? This cannot be undone.")) {
      return;
    }
    try {
      await factoryResetDevice(device.deviceId);
      dispatch(showSuccessSnackbar("Factory reset queued"));
    } catch (exception) {
      dispatch(showErrorSnackbar("Failed to queue factory reset"));
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" my={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!device) {
    return (
      <Card sx={{ width: "100%" }}>
        <CardHeader
          avatar={<RouterIcon color="disabled" />}
          title="No router linked"
          subheader="Link the customer's TR-069/CWMP-capable router by its serial number once it has checked in at least once."
        />
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              size="small"
              label="Router Serial Number"
              value={serialNumber}
              onChange={(event) => setSerialNumber(event.target.value)}
            />
            <Button
              variant="contained"
              disabled={isSubmitting || !serialNumber.trim()}
              onClick={handleLink}
            >
              Link Router
            </Button>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ width: "100%" }}>
      <CardHeader
        avatar={<RouterIcon color={device.online ? "success" : "disabled"} />}
        title={device.modelName || device.productClass || "Router"}
        subheader={`${device.manufacturer || ""} · SN ${device.serialNumber}`}
        action={
          <Chip
            label={device.online ? "ONLINE" : "OFFLINE"}
            color={device.online ? "success" : "default"}
            size="small"
            sx={{ fontWeight: 700 }}
          />
        }
      />
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>Software Version</TableCell>
                    <TableCell>{device.softwareVersion || "N/A"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Hardware Version</TableCell>
                    <TableCell>{device.hardwareVersion || "N/A"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>IP Address</TableCell>
                    <TableCell>{device.ipAddress || "N/A"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Last Checked In</TableCell>
                    <TableCell>
                      {device.lastInformAt
                        ? new Date(device.lastInformAt).toLocaleString()
                        : "Never"}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>
              Recent Tasks
            </Typography>
            {tasks && tasks.length ? (
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {tasks.slice(0, 5).map((task) => (
                      <TableRow key={task.taskId}>
                        <TableCell>{task.taskType}</TableCell>
                        <TableCell>
                          <Chip
                            label={task.status}
                            size="small"
                            color={
                              task.status === "COMPLETED"
                                ? "success"
                                : task.status === "FAILED"
                                  ? "error"
                                  : "default"
                            }
                          />
                        </TableCell>
                        <TableCell>{new Date(task.createdAt).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No tasks yet
              </Typography>
            )}
          </Grid>
        </Grid>

        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 3 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            disabled={isSubmitting}
            onClick={handleRefresh}
          >
            Refresh Parameters
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="warning"
            startIcon={<RestartAltIcon />}
            disabled={isSubmitting}
            onClick={handleReboot}
          >
            Reboot
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<SettingsBackupRestoreIcon />}
            disabled={isSubmitting}
            onClick={handleFactoryReset}
          >
            Factory Reset
          </Button>
          <Button
            size="small"
            variant="text"
            color="inherit"
            startIcon={<LinkOffIcon />}
            disabled={isSubmitting}
            onClick={handleUnlink}
          >
            Unlink
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default Tr069Device;
